// apps/admin/src/lib/wsClient.ts
//
// Admin-portal WebSocket shim — delegates to the CANONICAL client
// `connectWs` from `@printway/ui` (the merchant D-281 implementation that
// is verified working in prod) while preserving the legacy module surface
// (`subscribe` / `onConnectionState` / `getConnectionState` / `close` /
// `connectNow`) that admin consumers already import.
//
// Debug session dbg-20260703-020244-1493 (MASTER-FIX-PLAN 0.6, deep-scan
// Round 5 agent K): the previous implementation here spoke a LEGACY wire
// protocol the server silently drops —
//   1. outbound frames used `{op: 'subscribe'|'ping'}` while the server
//      (apps/api/src/modules/notifications/ws/server.ts) switches on
//      `msg.type` and ignores unknown frames → no subscription ever
//      registered;
//   2. channel names were legacy dot-style (`system.*`, `orders.*`) which
//      fail the server's default-deny `canSubscribe` RBAC — only
//      `ws:user:{id}` / `ws:merchant:{id}` / `ws:vendor:{id}` / `ws:admin` /
//      `ws:system` / `ws:broadcast:all` exist (channelRBAC.ts);
//   3. inbound dispatch read `frame.event` while the server emits
//      `{type:'event', channel, eventName, payload, seq}` → every event
//      dropped;
//   4. the reconnect ladder had no jitter → thundering herd on
//      POST /auth/ws-ticket after a server restart (429s).
// Delegating to `connectWs` fixes all four: canonical `type`-discriminated
// frames, ws-ticket auth flow, `eventName` dispatch, heartbeat with pong
// watchdog, and exponential backoff with ±20% jitter.
//
// Consumer contract (unchanged imports, clarified semantics):
//   `subscribe(eventName, handler)` — the key is a DOMAIN EVENT NAME
//   (e.g. `system.announcement.new`, `export.ready`,
//   `system.approval.requested`). This module owns the mapping from event
//   name → canonical server channel (`ws:system` / `ws:admin`; events on
//   the caller's own `ws:user:{userId}` channel need no explicit
//   subscription — the server auto-subscribes it at upgrade). Handlers
//   receive the legacy `WsFrame` shape with `event` = server `eventName`.
//
// vg-binding: PLAN:task-08
// vg-binding: P8.D-161
// vg-binding: P8.D-162
// vg-binding: P8.D-212
// vg-binding: P8.D-260
// vg-binding: P8.D-267
// vg-binding: CONTEXT:decisions           (P15.D-18 notification.new + version-floor, P15.D-07 membership events; Task 25)
// vg-binding: INTERFACE-STANDARDS:error-shape

import { connectWs, type WsClientHandle } from '@printway/ui';

// ---------------------------------------------------------------------------
// Public types (unchanged surface)
// ---------------------------------------------------------------------------

/**
 * Legacy frame shape delivered to admin handlers. `channel` is the
 * canonical server channel the event arrived on (e.g. `ws:system`),
 * `event` is the server `eventName` (e.g. `system.announcement.new`).
 */
export interface WsFrame<TPayload = unknown> {
  channel: string;
  event: string;
  payload: TPayload;
  timestamp?: number;
}

export type WsHandler<TPayload = unknown> = (frame: WsFrame<TPayload>) => void;

export type WsConnectionState =
  | 'idle'
  | 'connecting'
  | 'open'
  | 'reconnecting'
  | 'closed';

export type WsStateListener = (state: WsConnectionState) => void;

// ---------------------------------------------------------------------------
// Event → canonical channel map
// ---------------------------------------------------------------------------

/** Admin-tier channel — `ws:admin` (roleType ∈ {admin, superadmin}). */
const CHANNEL_ADMIN = 'ws:admin';
/** SA-only system channel — `ws:system` (roleType === superadmin). */
const CHANNEL_SYSTEM = 'ws:system';

/**
 * Which canonical channel carries each domain event, verified against the
 * server-side publish calls (source of truth):
 *   - `ws:system`  ← announcements.routes.ts (`system.announcement.new`),
 *                    dual-approval.routes.ts (`system.approval.*`),
 *                    channelRBAC.ts documents `system.emergency.changed` /
 *                    `system.feature_flag.changed` / `system.health.alert.*`
 *                    and `anomaly.detected` here (dispatch hooks pending).
 *   - `ws:admin`   ← maintenance-mode.service.ts (`system.maintenance.changed`).
 *   - `null`       ← events delivered on the caller's own `ws:user:{userId}`
 *                    channel (auto-subscribed by the server at upgrade):
 *                    `export.ready` (export-job.processor.ts),
 *                    `notification.*` (dispatcher / notification.handler).
 */
const EVENT_CHANNEL_MAP: Readonly<Record<string, string | null>> = {
  // ws:system — superadmin-only.
  'system.announcement.new': CHANNEL_SYSTEM,
  'anomaly.detected': CHANNEL_SYSTEM,
  'system.anomaly.detected': CHANNEL_SYSTEM,
  'system.approval.requested': CHANNEL_SYSTEM,
  'system.approval.voted': CHANNEL_SYSTEM,
  'system.approval.executed': CHANNEL_SYSTEM,
  'system.approval.rejected': CHANNEL_SYSTEM,
  'approval.expired': CHANNEL_SYSTEM,
  'system.health.alert': CHANNEL_SYSTEM,
  'system.health.alert.cleared': CHANNEL_SYSTEM,
  'system.emergency.changed': CHANNEL_SYSTEM,
  'system.feature_flag.changed': CHANNEL_SYSTEM,
  // ws:admin — admin + superadmin.
  'system.maintenance.changed': CHANNEL_ADMIN,
  'admin.invoice-summary-changed': CHANNEL_ADMIN,
  'system.activity': CHANNEL_ADMIN,
  // ws:user:{userId} — auto-subscribed server-side, no explicit subscribe.
  'export.ready': null,
  // Canonical notification signal (P15.D-18). The legacy `notification.create`
  // map entry was REMOVED in Task 29 with the server-side emitter (G-35); rides
  // ws:user:{userId}.
  'notification.new': null,
  'notification.read': null,
  'notification.read_all': null,
  // Membership lifecycle (P15.D-07) — delivered on the revoked/granted user's own
  // ws:user:{userId} channel; the FE busts the badge + refetches on receipt.
  'membership.revoked': null,
  'membership.granted': null,
};

/**
 * Resolve the canonical channel to subscribe for an event key. Unknown
 * `system.*` events default to `ws:system` (matches the channelRBAC
 * taxonomy comment); everything else defaults to `ws:admin`.
 */
function channelForEvent(eventName: string): string | null {
  if (eventName in EVENT_CHANNEL_MAP) {
    return EVENT_CHANNEL_MAP[eventName] ?? null;
  }
  return eventName.startsWith('system.') ? CHANNEL_SYSTEM : CHANNEL_ADMIN;
}

// ---------------------------------------------------------------------------
// Module-local state
// ---------------------------------------------------------------------------

let handle: WsClientHandle | null = null;
let connectionState: WsConnectionState = 'idle';

/** Connection-state listeners (banner UI, useWsConnectionStore). */
const stateListeners = new Set<WsStateListener>();

/** Canonical channel → count of active event subscriptions needing it. */
const channelRefs = new Map<string, number>();

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function hasWebSocket(): boolean {
  return (
    typeof window !== 'undefined' &&
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    typeof (window as any).WebSocket === 'function'
  );
}

function setState(next: WsConnectionState): void {
  if (connectionState === next) return;
  connectionState = next;
  for (const fn of stateListeners) {
    try {
      fn(next);
    } catch {
      /* listeners must not break the dispatcher */
    }
  }
}

/**
 * Lazily create the canonical client. `connectWs` owns the ws-ticket auth
 * flow (POST /api/v1/auth/ws-ticket → wsUrl + one-time ticket), heartbeat
 * (`{type:'ping', seq}` / pong watchdog), resubscribe-on-reconnect, and
 * jittered exponential backoff.
 */
function ensureHandle(): WsClientHandle | null {
  if (!hasWebSocket()) return null;
  if (handle) return handle;

  setState('connecting');
  const h = connectWs({ scope: 'admin' });
  handle = h;

  h.onLifecycle((ev) => {
    // Ignore events from a stale handle after close() re-created the client.
    if (handle !== h && ev.type !== 'close') return;
    if (ev.type === 'open') {
      setState('open');
    } else if (ev.type === 'close') {
      if (handle !== h || h.getState() === 'closed') {
        // Disposed via close() — terminal.
        if (handle === h) setState('closed');
      } else {
        // Anomalous close — canonical client schedules a reconnect.
        setState('reconnecting');
      }
    }
  });

  return h;
}

// ---------------------------------------------------------------------------
// Public API (surface unchanged)
// ---------------------------------------------------------------------------

/**
 * Subscribe `handler` to the domain event `eventName`. Ensures the
 * canonical channel carrying that event is subscribed server-side (RBAC
 * enforced by the BE — denials are logged by the canonical client and
 * silently ignored here). Returns an unsubscribe function that releases
 * the channel when no other subscriber needs it.
 *
 * Safe to call in non-browser environments — returns a no-op unsubscriber.
 */
export function subscribe<TPayload = unknown>(
  eventName: string,
  handler: WsHandler<TPayload>,
): () => void {
  const h = ensureHandle();
  if (!h) {
    return () => {
      /* no-op */
    };
  }

  const channel = channelForEvent(eventName);
  if (channel) {
    const refs = channelRefs.get(channel) ?? 0;
    channelRefs.set(channel, refs + 1);
    if (refs === 0) h.subscribe(channel);
  }

  const off = h.on(eventName, (ev) => {
    handler({
      channel: ev.channel,
      event: ev.eventName,
      payload: ev.payload as TPayload,
    });
  });

  let active = true;
  const boundHandle = h;
  return () => {
    if (!active) return;
    active = false;
    off();
    // Only release the channel if the handle wasn't torn down by close().
    if (channel && handle === boundHandle) {
      const refs = channelRefs.get(channel) ?? 0;
      if (refs <= 1) {
        channelRefs.delete(channel);
        boundHandle.unsubscribe(channel);
      } else {
        channelRefs.set(channel, refs - 1);
      }
    }
  };
}

/**
 * Subscribe to connection-state changes. Returns an unsubscribe
 * function. Listener fires immediately with the current state so the
 * caller can sync without an `if (state === undefined)` guard.
 */
export function onConnectionState(fn: WsStateListener): () => void {
  stateListeners.add(fn);
  try {
    fn(connectionState);
  } catch {
    /* ignore */
  }
  return () => {
    stateListeners.delete(fn);
  };
}

export function getConnectionState(): WsConnectionState {
  return connectionState;
}

/**
 * Force-close the socket and reset module state. Primarily called by
 * `useAuthStore.logout()` so the next session starts with a clean
 * subscription map. Existing subscriptions are dropped (matching the
 * legacy behaviour); the next `subscribe()`/`connectNow()` opens a fresh
 * connection.
 */
export function close(): void {
  const h = handle;
  handle = null;
  channelRefs.clear();
  if (h) {
    try {
      h.disconnect();
    } catch {
      /* ignore */
    }
  }
  setState('closed');
}

/**
 * Imperatively open the connection without subscribing. Useful for
 * boot-time warm-up (e.g. AppShell mounts the connection banner before
 * any page subscribes to a channel).
 */
export function connectNow(): void {
  ensureHandle();
}

export const wsClient = {
  subscribe,
  onConnectionState,
  getConnectionState,
  connectNow,
  close,
} as const;

export default wsClient;
